import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import {
  capitalizeFirstLetter,
  getPreviousTier,
  isUpgrading,
  planTiers,
} from "@/react/helpers/planHelpers";
import { usePlanPageVariant } from "@/react/hooks/usePlanPageVariant";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Plan } from "@circle-react/types";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { FeaturesList } from "../shared/FeaturesList";

const localeRoot =
  "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card";

const getGridClasses = (planTier: string, isTabLayout: boolean) => {
  const rowClasses = (() => {
    if (planTier === planTiers.enterprise) {
      return "md:grid-rows-6";
    }
    if (planTier === planTiers.professional) {
      return "md:grid-rows-5";
    }
    if (planTier === planTiers.basic || planTier === planTiers.business) {
      return "md:grid-rows-4";
    }
    return "";
  })();

  const baseClasses = isTabLayout
    ? "grid grid-cols-1 gap-y-2"
    : "grid auto-cols-max grid-flow-col gap-x-10 gap-y-2";

  return classNames(baseClasses, rowClasses);
};

interface PlanCardHighlightsProps {
  plan: Plan;
}

export const PlanCardHighlights = ({ plan }: PlanCardHighlightsProps) => {
  const { currentPlanTier, isCommunityOnTrial } = usePlanStatus();
  const { isCompactLayout, isMobileLayout } = usePlanPageVariant();
  const shouldHighlightFeatures = Boolean(
    isCommunityOnTrial ||
      (currentPlanTier && isUpgrading(currentPlanTier, plan.tier)),
  );

  const featuresListTitle = useMemo(() => {
    if (plan.tier === planTiers.basic) {
      return t(`${localeRoot}.core_features`);
    }

    if (plan.tier === planTiers.professional) {
      return t(`${localeRoot}.professional_features`);
    }

    return t(`${localeRoot}.everything_plus`, {
      tier: capitalizeFirstLetter(getPreviousTier(plan.tier) ?? ""),
    });
  }, [plan]);

  return (
    <div className="mt-2 px-0 sm:mt-6 sm:px-11">
      <div>
        <div className="mb-2 sm:mb-4">
          <Typography.LabelXs weight="semibold">
            {featuresListTitle}
          </Typography.LabelXs>
        </div>
        <div
          className={getGridClasses(
            plan.tier,
            isCompactLayout || isMobileLayout,
          )}
        >
          <FeaturesList
            plan={plan}
            shouldHighlightFeatures={shouldHighlightFeatures}
          />
        </div>
      </div>
    </div>
  );
};
